// GAP-MEET-UX-019 — the SINGLE definition of "what counts as a row on the
// IS Qualification Queue".
//
// Why this file exists: the /queue list (modules/queue/queue-list/queue-list.component.ts)
// and the sidebar Queue badge (shared/components/app-shell/app-shell.component.ts)
// each had their OWN copy of the row-selection rule — both were "every
// non-cancelled meeting". When GAP-MEET-UX-017 (drop upcoming) and
// GAP-MEET-UX-018 (collapse per opportunity) changed the list, the badge kept
// the old rule and the two disagreed on screen: the page read "7 in
// qualification" while the badge next to it read 14 (2026-07-30 walk).
//
// Two surfaces showing the same count must not derive it twice. Both now call
// `qualificationRowCount()` / the two helpers below, so any future change to
// what belongs in the queue moves both numbers at once.
//
// These helpers take the raw MeetingDto-ish wire object (`any`) rather than a
// typed DTO, matching both call sites' existing tolerance for the loosely
// shaped /Meeting payload.

import { isRepActionableSession } from './rep-actionable-session';
import { compareSessionOrder } from './session-order';
export { sessionOrderMs, compareSessionOrder } from './session-order';

/** Lowercased wire status, or '' when absent. */
function normaliseStatus(m: any): string {
    return (m?.status ?? '').toString().toLowerCase();
}

/**
 * Is this meeting part of the rep's QUALIFICATION workload?
 *
 * GAP-MEET-UX-017 (Prashant, 2026-07-30) — a future-dated Scheduled row has
 * nothing to qualify: the session hasn't happened, so there is no Evidence
 * Density, no DIA score, and nothing for Agent 2 to reach a verdict on. It
 * rendered "Awaiting session" / "Not scored" — a row the rep cannot act on,
 * inflating the queue count with pure noise. Upcoming sessions belong to
 * /sessions (My Sessions), the surface for "what's booked"; /queue is the
 * surface for "what needs a decision".
 *
 * Admitted:
 *   Completed          → held session, awaiting verdict
 *   Active             → in session now
 *   Scheduled + past   → empty room (nobody joined; needs the rep to act)
 *   Draft              → invite not sent (rep action pending)
 *
 * Excluded: Cancelled (dead) and Scheduled + future (nothing yet).
 *
 * Note the asymmetry is deliberate — a PAST Scheduled row IS workload, so the
 * whole `scheduled` status family cannot be filtered out.
 *
 * GAP-BACKEND-081 / SPEC-backend-048 AC-8c (2026-08-08) — this used to read
 * `sessionDisplayState(m) === 'No-show'`, which broke when AC-8a re-keyed
 * that helper's DISPLAY LABEL to Completed-attendance only (Scheduled-past
 * now reads `Upcoming`, not `No-show`). This function was never actually
 * asking "what does the tile say" — it was asking "does the rep need to act
 * on this", which is a DIFFERENT question that happens to share one arm
 * (Scheduled-past) with the old label rule. That question now has its own
 * primitive, `isRepActionableSession` (`rep-actionable-session.ts`), which
 * keeps BOTH arms: Scheduled-past (this SPEC's transition-window case, until
 * [[GAP-BACKEND-082]]'s sweeper lands) AND Completed-with-empty-attendance
 * (the post-populate / post-real-Play-close case this SPEC adds). The
 * "past Scheduled IS workload" invariant above still holds — it is just
 * evaluated by the new primitive rather than by proxy through the display
 * helper.
 *
 * A Scheduled row with NO start time is EXCLUDED: `isRepActionableSession`
 * only admits a Scheduled row when it can prove the start is past, so an
 * undated row is not actionable. Be aware that qualification-review's own
 * `deriveState()` takes the opposite view on the same input (undated →
 * no-show). Excluding is the safer half of that disagreement here — an
 * undated row gives the rep nothing to act on, and admitting it would
 * reintroduce exactly the unactionable row GAP-MEET-UX-017 removed.
 * Reconciling the two derivations into one helper is a follow-up.
 */
export function isQualificationWorkload(m: any): boolean {
    const status = normaliseStatus(m);
    if (status === 'cancelled' || status === 'canceled') { return false; }
    if (status === 'scheduled') {
        // Only the empty-room half of `Scheduled` is workload.
        return isRepActionableSession(m);
    }
    return true;
}

/** One collapsed opportunity: the representative session plus how many
 *  sessions collapsed into it. */
export interface OpportunityGroup {
    latest: any;
    sessionCount: number;
    /** Every session in the group, ORDERED OLDEST → NEWEST by session order (session-order.ts: held instant for finished sessions, else start time; AC-13).
     *
     *  GAP-MEET-UX-020 — the DIA trend arrow needs the opportunity's score
     *  HISTORY, not just its representative session: `latest` is the newest
     *  session in session order, which on a multi-session opportunity is often
     *  an unscored no-show/upcoming row while the score the rep cares about
     *  sits on the session before it. Exposing the ordered members lets the
     *  consumer pick "latest scored" and "the one scored before it" without
     *  re-deriving the grouping. */
    sessions: any[];
}

/**
 * Collapse a session-level workload list to ONE ENTRY PER OPPORTUNITY.
 *
 * GAP-MEET-UX-018 (Prashant, 2026-07-30) — /queue was rendering one row per
 * MeetingDto, so an opportunity with a 3-session history occupied 3 rows (the
 * 2026-07-30 walk showed Molbio System x4, PROGNOSYS x2, SpaceX x2). That is
 * the wrong altitude: the rep makes ONE qualify/advance decision per
 * OPPORTUNITY, informed by the whole session history — which is exactly what
 * /queue/:oppId (qualification review) renders as a tile strip. The queue is
 * the index of decisions pending, so it needs one row per decision.
 *
 * The grouping key is DealId — the opportunity — NOT the account name. One
 * account can legitimately run two opportunities in parallel (different
 * products/builds), and those are two independent decisions that must stay
 * two rows. Collapsing on account name would merge them and hide one.
 *
 * Sessions with NO DealId cannot be grouped (nothing to group ON) and stay
 * standalone entries, each keyed by its own meeting id. This is the honest
 * fallback: inferring an opportunity from a matching account name would
 * fabricate CRM linkage the wire never asserted (meet-UX CLAUDE.md hard rule
 * 7 — CRM is the source of truth for Deals). GAP-BACKEND-039-class backfill
 * of DealId on legacy meetings is what shrinks this bucket.
 *
 * The representative session is the LATEST in session order (session-order.ts, AC-13): the rep's decision
 * rests on the most recent evidence, so the row's chip / DIA score /
 * recommendation should describe where the opportunity stands NOW.
 *
 * Insertion order of first appearance is preserved so the collapse never
 * reshuffles the backend's ordering.
 */
export function collapseByOpportunity(meetings: any[]): OpportunityGroup[] {
    const groups = new Map<string, OpportunityGroup>();
    for (const m of meetings) {
        const dealId = (m?.dealId || '').toString();
        // No DealId → ungroupable; key on the meeting id so it stays its own
        // entry rather than collapsing every orphan into one bucket.
        const key = dealId ? `deal:${dealId}` : `meeting:${(m?.id || '').toString()}`;
        const existing = groups.get(key);
        if (!existing) {
            groups.set(key, { latest: m, sessionCount: 1, sessions: [m] });
            continue;
        }
        existing.sessionCount += 1;
        existing.sessions.push(m);
        if (compareSessionOrder(m, existing.latest) > 0) {
            existing.latest = m;
        }
    }
    // GAP-MEET-UX-020 — order each group's members oldest → newest so a
    // consumer reading score history walks them chronologically. Undated
    // sessions sort oldest (startMs → -Infinity); Array.prototype.sort is
    // stable (spec-guaranteed since ES2019), so equal-key members keep their
    // relative backend order. Group ORDER itself is untouched — still first
    // appearance, per the contract above.
    //
    // Compared, not subtracted: two undated members would give
    // `-Infinity - -Infinity` = NaN, an inconsistent comparator that can
    // scramble the order it was meant to preserve.
    for (const g of groups.values()) {
        if (g.sessions.length > 1) {
            g.sessions.sort(compareSessionOrder);
        }
    }
    return Array.from(groups.values());
}


// ─────────────────────────────────────────────────────────────────────────
// GAP-MEET-UX-030 — OPPORTUNITY-DRIVEN queue rows
// ─────────────────────────────────────────────────────────────────────────
//
// Prashant, 2026-07-30: "all opportunity will be in queue, but account group
// wise. For example we have latest opportunity created like Start Hospital, no
// session schedule yet, but we need to show it in Qualification Queue as 'No
// session yet', DIA score = 'not scored', Ivaan Recommendation = 'Schedule
// session'."
//
// This INVERTS the previous model. GAP-MEET-UX-018 built rows by grouping
// MEETINGS, which structurally cannot represent a zero-session opportunity: no
// meeting, no row. The queue's actual subject is the OPPORTUNITY — the unit the
// rep makes one decision about — and sessions are evidence hanging off it. So
// the opportunity list is now the spine and meetings are joined onto it.
//
// A useful consequence: the duplicate rows GAP-MEET-UX-018 fixed can no longer
// arise at all. One opportunity is one row by construction, not by collapsing.

/** An opportunity plus the qualification-relevant sessions joined to it. */
export interface OpportunityRowSource {
    /** The DealDto. */
    deal: any;
    /** Qualification-relevant sessions for this deal, OLDEST → NEWEST.
     *  Empty for a brand-new opportunity — that is a first-class state, not a
     *  missing-data case. */
    sessions: any[];
}

/**
 * Join opportunities to their sessions, preserving EVERY opportunity.
 *
 * The left side is authoritative: an opportunity with no qualification-relevant
 * sessions still yields a row (`sessions: []`), which is what makes the
 * "No session yet / Not scored / Schedule session" state reachable.
 *
 * Sessions are matched on `meeting.dealId === deal.id` — the only linkage the
 * wire asserts. A session whose DealId matches no supplied opportunity is
 * DROPPED rather than shown as an orphan row: the queue is a list of
 * opportunities, and a session pointing at an opportunity the rep cannot see
 * has no decision attached to it. (Before GAP-BACKEND-039-class DealId backfill
 * completes, legacy DealId-less meetings fall in this bucket — see
 * `orphanSessionCount` for surfacing that instead of hiding it.)
 */
export function joinOpportunitiesToSessions(deals: any[], meetings: any[]): OpportunityRowSource[] {
    // SPEC-web-planner-008 AC-17 (operator R139): a disqualified deal (closed-lost) leaves the Queue's open work and
    // its badge. AC-19 (R144) lists it again, outside every count, through closedLostOpportunities.
    return joinDeals((deals || []).filter(deal => !isClosedLost(deal)), meetings);
}

/** SPEC-web-planner-008 AC-19 (operator R144) — the closed-lost deals, joined to their sessions exactly as
 *  {@link joinOpportunitiesToSessions} joins the open ones. Shown under the Queue's Disqualify chip, never counted
 *  (AC-20): the badge and the counts keep reading joinOpportunitiesToSessions. */
export function closedLostOpportunities(deals: any[], meetings: any[]): OpportunityRowSource[] {
    return joinDeals((deals || []).filter(isClosedLost), meetings);
}

function joinDeals(deals: any[], meetings: any[]): OpportunityRowSource[] {
    const relevant = meetings.filter(isQualificationWorkload);
    const byDeal = new Map<string, any[]>();
    for (const m of relevant) {
        const key = (m?.dealId || '').toString();
        if (!key) { continue; }
        const bucket = byDeal.get(key);
        if (bucket) { bucket.push(m); } else { byDeal.set(key, [m]); }
    }
    return deals.map(deal => {
        const sessions = byDeal.get((deal?.id || '').toString()) || [];
        // Oldest → newest, so a consumer reading score history walks it
        // chronologically. Compared not subtracted: two undated sessions would
        // give -Infinity - -Infinity = NaN, an inconsistent comparator.
        if (sessions.length > 1) {
            sessions.sort(compareSessionOrder);
        }
        return { deal, sessions };
    });
}

/** SPEC-web-planner-008 AC-17 — the DealStage.ClosedLost wire value (a Disqualify sets it, AC-14). */
export const DEAL_STAGE_CLOSED_LOST = 'closed-lost';

export function isClosedLost(deal: any): boolean {
    return (deal?.stage ?? '').toString().trim().toLowerCase() === DEAL_STAGE_CLOSED_LOST;
}

/** How many qualification-relevant sessions could NOT be attached to any of
 *  the supplied opportunities (no DealId, or a DealId outside the list).
 *  Surfaced so the join never silently swallows a rep's session — a non-zero
 *  value means DealId backfill is incomplete, not that the queue is wrong. */
export function orphanSessionCount(deals: any[], meetings: any[]): number {
    const ids = new Set((deals || []).map(d => (d?.id || '').toString()).filter(Boolean));
    return meetings.filter(isQualificationWorkload)
        .filter(m => {
            const key = (m?.dealId || '').toString();
            return !key || !ids.has(key);
        }).length;
}

/**
 * Order rows ACCOUNT-GROUP WISE, needs-action first (Prashant, 2026-07-30).
 *
 * Two-level sort:
 *   1. Account blocks — every opportunity for one account sits together,
 *      blocks ordered by their most urgent member.
 *   2. Within a block — most urgent first, then opportunity name for stability.
 *
 * "Urgency" is the row's own `actionRank` (lower = needs the rep sooner), which
 * the caller supplies from its verdict projection so this function stays free
 * of any copy/verdict knowledge.
 *
 * Stable and total: ties fall through to account name then opportunity name, so
 * the order never depends on backend row order or on JS sort stability.
 */
export function sortAccountGroupedByUrgency<T extends {
    accountName: string;
    opportunityName: string;
    actionRank: number;
}>(rows: T[]): T[] {
    const mostUrgentByAccount = new Map<string, number>();
    for (const r of rows) {
        const key = accountKey(r.accountName);
        const current = mostUrgentByAccount.get(key);
        if (current === undefined || r.actionRank < current) {
            mostUrgentByAccount.set(key, r.actionRank);
        }
    }
    return [...rows].sort((a, b) => {
        const aKey = accountKey(a.accountName);
        const bKey = accountKey(b.accountName);
        if (aKey !== bKey) {
            // Block ordering: the account with the most urgent row leads.
            const au = mostUrgentByAccount.get(aKey) ?? Number.MAX_SAFE_INTEGER;
            const bu = mostUrgentByAccount.get(bKey) ?? Number.MAX_SAFE_INTEGER;
            if (au !== bu) { return au - bu; }
            return aKey.localeCompare(bKey);
        }
        // Same account: urgency, then name.
        if (a.actionRank !== b.actionRank) { return a.actionRank - b.actionRank; }
        return (a.opportunityName || '').localeCompare(b.opportunityName || '');
    });
}

/** Case/whitespace-insensitive account identity, so "PROGNOSYS MEDICAL" and
 *  "Prognosys Medical" group as one block rather than two. */
function accountKey(name: string): string {
    return (name || '').trim().toLowerCase();
}

/**
 * The number the /queue page shows and the sidebar badge shows — ONE
 * derivation, so they cannot drift (GAP-MEET-UX-019).
 *
 * GAP-MEET-UX-030 — this is now an OPPORTUNITY count: every opportunity is a
 * row, including those with no sessions yet. Callers that have only the meeting
 * list (no deals) get the legacy meeting-collapse count instead; the sidebar
 * badge passes both so it matches the page.
 */
export function qualificationRowCount(meetings: any[], deals?: any[]): number {
    if (deals) {
        return joinOpportunitiesToSessions(deals, meetings).length;
    }
    return collapseByOpportunity(meetings.filter(isQualificationWorkload)).length;
}
